import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar as BigCalendar, dateFnsLocalizer, Views } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { format, parse, startOfWeek, getDay, addDays } from "date-fns";
import enAU from "date-fns/locale/en-AU";
import { Loader2, X, User, Phone, MapPin, Mail, Trash2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";

const locales = { "en-AU": enAU };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });
const DnDCalendar = withDragAndDrop(BigCalendar);

// 5-stage status enum → colour
const STATUS_META = {
  outstanding: { label: "Outstanding", bg: "#FF9500", fg: "#000" },
  scheduled:   { label: "Scheduled",   bg: "#0A84FF", fg: "#fff" },
  in_progress: { label: "In Progress", bg: "#FFCC00", fg: "#000" },
  completed:   { label: "Completed",   bg: "#34C759", fg: "#000" },
  invoiced:    { label: "Invoiced",    bg: "#71717A", fg: "#fff" },
};

const ALL_STATUSES = Object.keys(STATUS_META);

function jobToEvent(j) {
  if (!j.scheduled_at) return null; // outstanding jobs without a date aren't on the grid
  const start = new Date(j.scheduled_at);
  if (isNaN(start.getTime())) return null;
  const end = new Date(start.getTime() + (j.duration_minutes || 60) * 60000);
  return {
    id: j.id,
    title: `${j.customer_name || "—"} · ${j.service}`,
    start,
    end,
    resource: j,
  };
}

export default function JobsCalendar() {
  const [view, setView] = useState(Views.WEEK);
  const [date, setDate] = useState(new Date());
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [visibleStatuses, setVisibleStatuses] = useState(
    () => new Set(ALL_STATUSES.filter((s) => s !== "invoiced"))
  );
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Pull a wide window — 30 days back, 90 days forward — for snappy nav.
      const start = addDays(new Date(), -30).toISOString();
      const end = addDays(new Date(), 90).toISOString();
      const r = await api.get(`/admin/jobs`, { params: { start, end } });
      setJobs(r.data || []);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Failed to load jobs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const events = useMemo(() => {
    return jobs
      .filter((j) => visibleStatuses.has(j.status))
      .map(jobToEvent)
      .filter(Boolean);
  }, [jobs, visibleStatuses]);

  const eventPropGetter = useCallback((event) => {
    const meta = STATUS_META[event.resource.status] || STATUS_META.outstanding;
    return {
      style: {
        backgroundColor: meta.bg,
        color: meta.fg,
        borderRadius: 0,
        border: "none",
        fontSize: "12px",
        padding: "2px 6px",
        fontWeight: 600,
      },
    };
  }, []);

  const onEventDrop = useCallback(async ({ event, start, end }) => {
    const duration = Math.max(15, Math.round((end - start) / 60000));
    // Optimistic update
    setJobs((prev) => prev.map((j) =>
      j.id === event.id ? { ...j, scheduled_at: start.toISOString(), duration_minutes: duration } : j
    ));
    try {
      await api.patch(`/admin/jobs/${event.id}`, {
        scheduled_at: start.toISOString(),
        duration_minutes: duration,
      });
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Reschedule failed");
      load();
    }
  }, [load]);

  const onEventResize = useCallback(async ({ event, start, end }) => {
    const duration = Math.max(15, Math.round((end - start) / 60000));
    setJobs((prev) => prev.map((j) =>
      j.id === event.id ? { ...j, scheduled_at: start.toISOString(), duration_minutes: duration } : j
    ));
    try {
      await api.patch(`/admin/jobs/${event.id}`, {
        scheduled_at: start.toISOString(),
        duration_minutes: duration,
      });
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Resize failed");
      load();
    }
  }, [load]);

  const onSelectSlot = useCallback(() => {
    // For now we don't open a creation modal here — admin creates jobs from the
    // customer panel. A future enhancement could open a quick-create dialog.
  }, []);

  const updateJob = async (id, patch) => {
    try {
      const r = await api.patch(`/admin/jobs/${id}`, patch);
      setJobs((prev) => prev.map((j) => (j.id === id ? r.data : j)));
      setSelected(r.data);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Update failed");
    }
  };

  const removeJob = async () => {
    if (!selected) return;
    if (!window.confirm("Delete this job?")) return;
    try {
      await api.delete(`/admin/customers/${selected.customer_id}/jobs/${selected.id}`);
      setJobs((prev) => prev.filter((j) => j.id !== selected.id));
      setSelected(null);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const toggleStatusFilter = (s) => {
    setVisibleStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  };

  return (
    <div className="space-y-4" data-testid="admin-jobs-calendar">
      <div className="flex flex-wrap items-center gap-2">
        <div className="te-overline text-[10px] mr-2">Show:</div>
        {ALL_STATUSES.map((s) => {
          const meta = STATUS_META[s];
          const on = visibleStatuses.has(s);
          return (
            <button
              key={s}
              onClick={() => toggleStatusFilter(s)}
              data-testid={`calendar-filter-${s}`}
              className="px-2.5 py-1 text-[11px] te-overline transition-opacity"
              style={{
                background: meta.bg,
                color: meta.fg,
                opacity: on ? 1 : 0.25,
                borderRadius: 0,
              }}
            >
              {meta.label}
            </button>
          );
        })}
        <button onClick={load} className="te-btn te-btn-ghost text-xs ml-auto" data-testid="calendar-refresh">
          {loading ? <Loader2 size={12} className="animate-spin" /> : "Refresh"}
        </button>
      </div>

      {err && (
        <div className="te-card p-3 text-sm" style={{ color: "var(--te-primary)" }} data-testid="calendar-error">
          {err}
        </div>
      )}

      <div
        className="te-card"
        style={{ height: "70vh", background: "var(--te-paper)", padding: "12px" }}
        data-testid="calendar-grid"
      >
        <DnDCalendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
          view={view}
          onView={setView}
          date={date}
          onNavigate={setDate}
          onEventDrop={onEventDrop}
          onEventResize={onEventResize}
          resizable
          selectable
          onSelectSlot={onSelectSlot}
          onSelectEvent={(ev) => setSelected(ev.resource)}
          eventPropGetter={eventPropGetter}
          step={30}
          timeslots={2}
          defaultView={Views.WEEK}
          culture="en-AU"
          style={{ height: "100%", color: "#111" }}
        />
      </div>

      {selected && (
        <JobDetailDrawer
          job={selected}
          onClose={() => setSelected(null)}
          onPatch={(patch) => updateJob(selected.id, patch)}
          onDelete={removeJob}
        />
      )}

      <CalendarStyleOverrides />
    </div>
  );
}

function JobDetailDrawer({ job, onClose, onPatch, onDelete }) {
  const meta = STATUS_META[job.status] || STATUS_META.outstanding;
  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
      data-testid="job-detail-drawer"
    >
      <div
        className="w-full max-w-md h-full overflow-y-auto p-6"
        style={{ background: "var(--te-bg)", borderLeft: "1px solid var(--te-border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="te-overline text-[10px]">Job details</div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="job-detail-close">
            <X size={18} />
          </button>
        </div>
        <h3 className="text-xl font-bold tracking-tight mb-3">{job.service}</h3>
        <div
          className="inline-block px-2 py-1 text-[11px] te-overline mb-4"
          style={{ background: meta.bg, color: meta.fg }}
        >
          {meta.label}
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-zinc-300"><User size={13} /> {job.customer_name || "—"}</div>
          {job.customer_phone && (
            <a href={`tel:${job.customer_phone}`} className="flex items-center gap-2 text-zinc-400 hover:text-white">
              <Phone size={13} /> {job.customer_phone}
            </a>
          )}
          {job.customer_email && (
            <a href={`mailto:${job.customer_email}`} className="flex items-center gap-2 text-zinc-400 hover:text-white">
              <Mail size={13} /> {job.customer_email}
            </a>
          )}
          {job.suburb && (
            <div className="flex items-center gap-2 text-zinc-400"><MapPin size={13} /> {job.suburb}</div>
          )}
          {job.scheduled_at && (
            <div className="text-zinc-400 text-xs mt-2">
              {new Date(job.scheduled_at).toLocaleString("en-AU")} · {job.duration_minutes || 60} min
            </div>
          )}
        </div>

        <div className="mt-6">
          <div className="te-overline text-[10px] mb-2">Update status</div>
          <div className="grid grid-cols-2 gap-2">
            {ALL_STATUSES.map((s) => {
              const m = STATUS_META[s];
              const active = job.status === s;
              return (
                <button
                  key={s}
                  onClick={() => onPatch({ status: s })}
                  data-testid={`job-status-${s}`}
                  className="px-3 py-2 text-xs te-overline transition-opacity"
                  style={{
                    background: m.bg,
                    color: m.fg,
                    opacity: active ? 1 : 0.5,
                    borderRadius: 0,
                  }}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>

        {job.customer_notes && (
          <div className="mt-6">
            <div className="te-overline text-[10px] mb-2">Your service notes (customer)</div>
            <p className="text-sm text-zinc-300 whitespace-pre-wrap">{job.customer_notes}</p>
          </div>
        )}
        {job.internal_notes && (
          <div className="mt-4">
            <div className="te-overline text-[10px] mb-2">Internal notes</div>
            <p className="text-sm text-zinc-300 whitespace-pre-wrap">{job.internal_notes}</p>
          </div>
        )}

        {typeof job.cost_total === "number" && (
          <div className="mt-6">
            <div className="te-overline text-[10px] mb-2">Cost</div>
            <div className="text-2xl font-bold">${job.cost_total.toFixed(2)}</div>
          </div>
        )}

        <button
          onClick={onDelete}
          className="te-btn te-btn-ghost text-sm mt-8 text-red-400 hover:text-red-300"
          data-testid="job-detail-delete"
        >
          <Trash2 size={13} /> Delete job
        </button>
      </div>
    </div>
  );
}

function CalendarStyleOverrides() {
  // Tame react-big-calendar's default light theme to match Tech Emergency's dark UI.
  return (
    <style>{`
      .rbc-calendar { background: var(--te-paper); color: #111; font-family: inherit; }
      .rbc-toolbar button { color: #fff; background: var(--te-bg); border: 1px solid var(--te-border); border-radius: 0; }
      .rbc-toolbar button.rbc-active { background: var(--te-primary); color: #fff; border-color: var(--te-primary); }
      .rbc-toolbar-label { color: #fff; font-weight: 600; letter-spacing: -0.01em; }
      .rbc-header { background: var(--te-bg); color: #fff; border-bottom: 1px solid var(--te-border); padding: 8px 4px; font-size: 11px; letter-spacing: 0.05em; text-transform: uppercase; }
      .rbc-month-view, .rbc-time-view, .rbc-time-header-content, .rbc-time-content, .rbc-month-row, .rbc-day-bg { border-color: var(--te-border) !important; }
      .rbc-day-bg { background: #0a0a0a; }
      .rbc-off-range-bg { background: #050505; }
      .rbc-today { background: rgba(255,59,48,0.08) !important; }
      .rbc-time-slot, .rbc-timeslot-group { border-color: var(--te-border) !important; }
      .rbc-time-gutter, .rbc-label, .rbc-date-cell { color: #aaa; }
      .rbc-event { box-shadow: none !important; }
      .rbc-show-more { color: var(--te-primary); background: transparent; }
      .rbc-agenda-view table.rbc-agenda-table { color: #ddd; }
      .rbc-agenda-view table.rbc-agenda-table thead > tr > th { background: var(--te-bg); color: #fff; border-bottom: 1px solid var(--te-border); }
      .rbc-agenda-view table.rbc-agenda-table tbody > tr > td { border-color: var(--te-border); }
    `}</style>
  );
}
